import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Form, Button, Alert, Card, Row, Col, Spinner } from 'react-bootstrap'
import {
  useAdminDataAccess,
  useCreateAdminDataAccess,
  useUpdateAdminDataAccess,
  useDeleteAdminDataAccess
} from 'api/hooks/use-admin-data-accesses'

const DataAccessForm = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const isEditing = !!id

  const { data: dataAccess, isLoading } = useAdminDataAccess({ id })
  const createMutation = useCreateAdminDataAccess({ options: { onSuccess: () => navigate('/admin/data-accesses') } })
  const updateMutation = useUpdateAdminDataAccess({ id, options: { onSuccess: () => navigate('/admin/data-accesses') } })
  const deleteMutation = useDeleteAdminDataAccess({ options: { onSuccess: () => navigate('/admin/data-accesses') } })

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    table_id: '',
    access_type: '',
    access: '',
    permission_category: '',
    permission_key: '',
    do_toggle: false,
    top_module_code: '',
    sub_module_code: '',
    top_module_name: ''
  })

  useEffect(() => {
    if (dataAccess) {
      setFormData({
        name: dataAccess.name || '',
        description: dataAccess.description || '',
        category: dataAccess.category || '',
        table_id: dataAccess.table_id || '',
        access_type: dataAccess.access_type || '',
        access: dataAccess.access || '',
        permission_category: dataAccess.permission_category || '',
        permission_key: dataAccess.permission_key || '',
        do_toggle: dataAccess.do_toggle || false,
        top_module_code: dataAccess.top_module_code || '',
        sub_module_code: dataAccess.sub_module_code || '',
        top_module_name: dataAccess.top_module_name || ''
      })
    }
  }, [dataAccess])

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (isEditing) {
      updateMutation.mutate(formData)
    } else {
      createMutation.mutate(formData)
    }
  }

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this data access?')) {
      deleteMutation.mutate(id)
    }
  }

  const error = createMutation.error || updateMutation.error || deleteMutation.error
  const isSubmitting = createMutation.isLoading || updateMutation.isLoading || deleteMutation.isLoading

  if (isLoading && isEditing) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    )
  }

  return (
    <div>
      <h2>{isEditing ? 'Edit Data Access' : 'Create Data Access'}</h2>

      {error && <Alert variant="danger" className="mt-3">{error.message || 'An error occurred'}</Alert>}

      <Card className="mt-3">
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Category *</Form.Label>
                  <Form.Control type="text" name="category" value={formData.category} onChange={handleChange} required />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Name</Form.Label>
                  <Form.Control type="text" name="name" value={formData.name} onChange={handleChange} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Table ID</Form.Label>
                  <Form.Control type="text" name="table_id" value={formData.table_id} onChange={handleChange} />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control as="textarea" rows={2} name="description" value={formData.description} onChange={handleChange} />
            </Form.Group>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Access Type</Form.Label>
                  <Form.Control type="text" name="access_type" value={formData.access_type} onChange={handleChange} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Access</Form.Label>
                  <Form.Control type="text" name="access" value={formData.access} onChange={handleChange} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Check type="checkbox" name="do_toggle" label="Do Toggle" checked={formData.do_toggle} onChange={handleChange} className="mt-4" />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Permission Category</Form.Label>
                  <Form.Control type="text" name="permission_category" value={formData.permission_category} onChange={handleChange} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Permission Key</Form.Label>
                  <Form.Control type="text" name="permission_key" value={formData.permission_key} onChange={handleChange} />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Top Module Code</Form.Label>
                  <Form.Control type="text" name="top_module_code" value={formData.top_module_code} onChange={handleChange} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Sub Module Code</Form.Label>
                  <Form.Control type="text" name="sub_module_code" value={formData.sub_module_code} onChange={handleChange} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Top Module Name</Form.Label>
                  <Form.Control type="text" name="top_module_name" value={formData.top_module_name} onChange={handleChange} />
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex justify-content-between mt-4">
              <div>
                <Button type="submit" variant="primary" disabled={isSubmitting} className="me-2">
                  {isSubmitting ? 'Saving...' : isEditing ? 'Update' : 'Create'}
                </Button>
                <Button type="button" variant="light-primary" onClick={() => navigate('/admin/data-accesses')} disabled={isSubmitting}>Cancel</Button>
              </div>
              {isEditing && (
                <Button type="button" variant="danger" onClick={handleDelete} disabled={isSubmitting}>Delete Data Access</Button>
              )}
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  )
}

export default DataAccessForm
